import { useEffect, useRef } from "react";
import { useTrans } from "@/utils/useTrans";
import ComponentErrorBoundary from "@/v2-components/pages/ComponentErrorBoundary";

const CLIPS = [
  "https://cdn.bhaptics.com/media/live/live-intoduce-video-jangmagun-opt.mp4",
  "https://cdn.bhaptics.com/media/live/live-intoduce-video-rwyche-opt.mp4",
] as const;

const VOICE_MATRIX_KEYS = [
  { quoteKey: "live_clips_quote_1", authorKey: "live_clips_author_1" },
  { quoteKey: "live_clips_quote_2", authorKey: "live_clips_author_2" },
  { quoteKey: "live_clips_quote_3", authorKey: "live_clips_author_3" },
  { quoteKey: "live_clips_quote_4", authorKey: "live_clips_author_4" },
  { quoteKey: "live_clips_quote_5", authorKey: "live_clips_author_5" },
  { quoteKey: "live_clips_quote_6", authorKey: "live_clips_author_6" },
] as const;

function ClipVideo({ src }: { src: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-[16px] bg-coolgray-10 md:flex-1">
      <video
        ref={videoRef}
        src={src}
        muted
        loop
        playsInline
        className="h-full w-full object-cover"
      />
    </div>
  );
}

function VoiceCard({ quote, author }: { quote: string; author: string }) {
  return (
    <div className="flex h-[108px] w-[400px] shrink-0 flex-col justify-between rounded-[16px] bg-white px-[28px] py-[24px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.15)] md:h-[124px]">
      <p className="text-base tracking-[0.25px] text-coolgray-70 md:text-lg">
        {quote}
      </p>
      <span className="text-sm font-600 tracking-[0.4px] text-blue-60">
        {author}
      </span>
    </div>
  );
}

export default function LiveClipsSection() {
  const { t } = useTrans(["software"]);
  const voiceMatrix = VOICE_MATRIX_KEYS.map((k) => ({
    quote: t(k.quoteKey),
    author: t(k.authorKey),
  }));

  return (
    <ComponentErrorBoundary>
      <section className="flex w-full flex-col gap-6 overflow-hidden md:gap-10">
        {/* Clips 영역 — 2 video panels, 16:9 */}
        <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-[16px] px-4 md:flex-row md:gap-[24px]">
          {CLIPS.map((src, i) => (
            <ClipVideo key={i} src={src} />
          ))}
        </div>

        {/* Voice Matrix — infinite auto-scroll marquee */}
        <div
          className="mx-auto w-full max-w-[1920px] overflow-x-clip py-[6px]"
          style={{
            maskImage:
              "linear-gradient(to right, transparent, black 160px, black calc(100% - 160px), transparent)",
          }}
        >
          <div className="live-marquee-track">
            {[...voiceMatrix, ...voiceMatrix].map((item, i) => (
              <VoiceCard key={i} quote={item.quote} author={item.author} />
            ))}
          </div>
        </div>
      </section>
    </ComponentErrorBoundary>
  );
}
